import { gql, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'

interface IQueryDataType {
  listActiveItems: {
    nodes: {
      id: number
      parentId: number
      name: string
      type: string
    }[]
  }
}

export interface IDataType {
  items?: {
    options: any
    value: any
  }
}

const QUERY_NAME = 'listActiveItems'
const query = gql`
  query (
    $iStartDate: Date!
    $iEndDate: Date!
    $iParams: JSON!
    $iRoot: String!
  ) {
    ${QUERY_NAME}(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: $iParams
      iRoot: $iRoot
    ) {
      nodes {
        id
        parentId
        name
        type
      }
    }
  }
`

export interface IOptionType {
  initialSelection: string[]
  metrics: string[]
  categoriesOnly?: boolean
  root?: string
}

const useItemOrCategoryV2 = ({
  initialSelection,
  metrics,
  categoriesOnly,
  root,
}: IOptionType): IDataType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const { data } = useQuery<IQueryDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iParams: {
        location_group_ids: groupFilter?.ids,
        metrics,
      },
      iRoot: root ?? '',
    },
    skip: !startDate || !endDate || !groupFilter,
  })
  const itemHook = useState([initialSelection])

  return {
    items: useMemo(() => {
      const fetchedData = data?.[QUERY_NAME]?.nodes

      // Build a lookup for category names by id
      const categoryNameById: {
        [id: string]: string
      } = {}
      fetchedData?.forEach(({ id, name, type }) => {
        if (type === 'CATEGORY') {
          categoryNameById[id.toString()] = name
        }
      })

      // Top-level category options
      const categoriesOptions = Object.keys(categoryNameById).map(
        (categoryId) => ({
          id: categoryId,
          parentId: 'root',
          displayName: categoryNameById[categoryId],
        }),
      )

      // "All items in category" options
      const allCategoriesOptions = categoriesOptions.map(
        ({ id, displayName }) => ({
          id: `category-${id}`,
          parentId: id,
          displayName: `All ${displayName}`,
        }),
      )

      // Individual item options
      const options =
        fetchedData
          ?.filter(({ type }) => type === 'ITEM')
          .map(({ id, name, parentId }) => ({
            id,
            parentId: parentId.toString(),
            displayName: name,
          })) ?? []

      const allOptions = [
        ...categoriesOptions,
        ...allCategoriesOptions,
        ...options,
      ]

      return {
        value: itemHook[0],
        options: categoriesOnly ? categoriesOptions : allOptions,
        onChange: itemHook[1],
      }
    }, [data, itemHook, categoriesOnly]),
  }
}

export default useItemOrCategoryV2
